import { useState } from "react";
import { type PersonaDef } from "../data/org";
import { useRH, type Status } from "../store/rh";
import { useUI } from "../store/ui";
import { initials, monogramColor } from "../lib/monogram";
import { ReviewModal } from "./ReviewModal";
import { AgentPromptEditor } from "./AgentPromptEditor";
import { profileOf } from "../data/profiles";
import { useSettings } from "../store/settings";
import { generateImageGemini, generateImageOpenAI } from "../lib/generators";
import { resizeForAvatar } from "../lib/imageResize";
import { avatarPersonaPrompt } from "../lib/promptTemplates";

export function PersonaCard({ persona }: { persona: PersonaDef }) {
  const employee = useRH(s => s.employees[persona.id]);
  const salary   = useRH(s => s.effectiveSalary(persona.id));
  const hire     = useRH(s => s.hire);
  const fire     = useRH(s => s.fire);
  const rehire   = useRH(s => s.rehire);
  const setSalary= useRH(s => s.setSalary);
  const setNote  = useRH(s => s.setNote);
  const setAvatar= useRH(s => s.setAvatar);
  const resetSlot= useRH(s => s.resetSlot);
  const settings = useSettings();

  const filter   = useUI(s => s.filter);
  const search   = useUI(s => s.search.trim().toLowerCase());
  const inMeet   = useUI(s => s.meetingPicks.includes(persona.id));
  const toggleM  = useUI(s => s.toggleMeeting);

  const [editing, setEditing] = useState(false);
  const [draftSal, setDraftSal] = useState(salary);
  const [openNote, setOpenNote] = useState(false);
  const [noteDraft, setNoteDraft] = useState(employee?.notes ?? "");
  const [showReview, setShowReview] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const reviewCount = employee?.reviews?.length ?? 0;
  const lastRating = employee?.reviews?.[0]?.rating;
  const [avLoading, setAvLoading] = useState(false);

  const generateAvatar = async () => {
    if (!settings.geminiKey && !settings.openaiKey) {
      alert("Configure uma chave Gemini ou OpenAI em ⚙️ primeiro.");
      return;
    }
    setAvLoading(true);
    try {
      const prompt = avatarPersonaPrompt({ name: persona.name, bio: persona.bio });
      const provider = settings.imageProvider;
      const model = provider === "gemini" ? settings.geminiImageModel : settings.openaiImageModel;
      const apiKey = provider === "gemini" ? settings.geminiKey : settings.openaiKey;
      if (!apiKey) {
        alert(`Sem chave ${provider}. Use o outro provedor ou configure em ⚙️.`);
        return;
      }
      const fn = provider === "gemini" ? generateImageGemini : generateImageOpenAI;
      const out = await fn({ apiKey, model, prompt, aspect: "1:1" });
      let final = out.dataUrl;
      try { final = await resizeForAvatar(out.dataUrl); } catch {}
      setAvatar(persona.id, final);
    } catch (e) {
      alert("Falha na geração: " + (e as Error).message);
    } finally {
      setAvLoading(false);
    }
  };

  const status: Status = employee?.status ?? "available";

  // filters — early return ANTES de declarar outras vars
  if (filter !== "all" && status !== filter) return null;
  if (search && !`${persona.name} ${persona.bio}`.toLowerCase().includes(search)) return null;

  const hasAvatar = !!employee?.avatarUrl;
  const isDraggable = status === "hired";
  const onDragStart = (e: React.DragEvent) => {
    if (!isDraggable) { e.preventDefault(); return; }
    e.dataTransfer.setData("text/persona", persona.id);
    e.dataTransfer.effectAllowed = "move";
  };
  const wasMoved = !!employee?.slotOverride && employee.slotOverride !== persona.slotId;

  const onHire = () => {
    const err = hire(persona.id);
    if (err) alert(err);
  };
  const onFire = () => {
    const reason = prompt(`Motivo da demissão de ${persona.name}? (opcional)`, "") || undefined;
    fire(persona.id, reason);
  };
  const onRehire = () => {
    const err = rehire(persona.id);
    if (err) alert(err);
  };
  const commitSalary = () => {
    const n = Number(draftSal);
    if (Number.isFinite(n) && n >= 0) setSalary(persona.id, Math.round(n));
    setEditing(false);
  };

  const ring =
    status === "hired" ? "border-ink bg-bone shadow-[3px_3px_0_0_#121214]" :
    status === "fired" ? "border-graymd bg-bone/60 text-graydk"
                       : "border-graydk bg-bone";

  const mono = monogramColor(persona.id);

  return (
    <div
      className={`relative border-2 ${ring} p-3 group transition
        ${isDraggable ? "cursor-grab active:cursor-grabbing" : ""}`}
      draggable={isDraggable}
      onDragStart={onDragStart}
    >
      <div className="flex items-start gap-3">
        {/* avatar / monogram */}
        <button
          onClick={generateAvatar}
          disabled={avLoading}
          title={hasAvatar ? "regenerar avatar IA" : "gerar avatar IA"}
          className="shrink-0 w-12 h-12 relative group overflow-hidden border border-ink/30"
          style={!hasAvatar ? { background: mono } : {}}
        >
          {hasAvatar ? (
            <img src={employee!.avatarUrl} alt={persona.name} className="w-full h-full object-cover" />
          ) : (
            <span className="absolute inset-0 flex items-center justify-center font-display font-black text-lg select-none text-bone">
              {initials(persona.name)}
            </span>
          )}
          {avLoading && (
            <span className="absolute inset-0 bg-ink/70 flex items-center justify-center font-mono text-[9px] text-bone">
              gerando…
            </span>
          )}
          <span className="absolute bottom-0 right-0 bg-ink text-bone text-[8px] px-0.5 opacity-0 group-hover:opacity-100">
            ✦
          </span>
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className={`font-display font-bold text-xl leading-tight uppercase truncate
              ${status === "fired" ? "line-through decoration-red" : ""}`}>
              {persona.name}
            </div>
            <div className="tag text-graydk shrink-0">{persona.power}/10</div>
          </div>
          <div className="mt-1 font-crimson italic text-[13px] leading-snug text-paper line-clamp-2">
            {persona.bio}
          </div>
          {/* skills chips */}
          <div className="mt-1.5 flex flex-wrap gap-1">
            {profileOf(persona.id, persona.tier).skills.slice(0, 3).map(sk => (
              <span key={sk} className="tag bg-ink/10 text-ink px-1.5 py-0.5">{sk}</span>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
        {/* salary inline edit */}
        <div className="tag text-graydk flex items-center gap-1">
          USD
          {editing ? (
            <input
              autoFocus
              type="number"
              value={draftSal}
              onChange={(e) => setDraftSal(Number(e.target.value))}
              onBlur={commitSalary}
              onKeyDown={(e) => { if (e.key === "Enter") commitSalary(); if (e.key === "Escape") setEditing(false); }}
              className="w-24 border border-ink bg-bone px-1 py-0.5 font-mono text-[11px]"
            />
          ) : (
            <button
              onClick={() => { setDraftSal(salary); setEditing(true); }}
              className="font-mono underline decoration-dotted hover:text-red"
              title="Editar salário"
            >
              {salary.toLocaleString("en-US")}
            </button>
          )}
          <span>/mês</span>
        </div>

        <div className="flex gap-2 items-center flex-wrap justify-end">
          {status === "hired" && (
            <button
              onClick={() => setShowReview(true)}
              className="btn btn-fire"
              title="Performance review"
            >
              ★ {reviewCount > 0 ? `${lastRating}/5` : "avaliar"}
            </button>
          )}
          {status === "hired" && (
            <button
              onClick={() => setShowPrompt(true)}
              className={`btn ${employee?.systemPromptOverride ? "bg-gold text-ink border-gold" : "btn-fire"}`}
              title="Editar system prompt do agente"
            >
              🎭 prompt
            </button>
          )}
          {status === "hired" && (
            <button
              className={`btn ${inMeet ? "bg-gold border-gold text-ink" : "btn-fire"}`}
              onClick={() => toggleM(persona.id)}
              title="Selecionar para reunião"
            >
              {inMeet ? "✓ reunião" : "+ reunião"}
            </button>
          )}
          {status === "available" && <button className="btn btn-hire" onClick={onHire}>+ contratar</button>}
          {status === "hired"     && <button className="btn btn-fire" onClick={onFire}>demitir</button>}
          {status === "fired"     && <button className="btn btn-hire" onClick={onRehire}>recontratar</button>}
        </div>
      </div>

      {employee?.fireReason && status === "fired" && (
        <div className="mt-2 text-[11px] font-mono text-graydk italic">
          motivo: {employee.fireReason}
        </div>
      )}

      {wasMoved && (
        <div className="mt-1 flex items-center justify-between text-[11px] font-mono">
          <span className="text-gold">✦ remanejado</span>
          <button onClick={() => resetSlot(persona.id)} className="underline text-graydk">
            voltar ao slot original
          </button>
        </div>
      )}

      {status === "hired" && (
        <div className="mt-2 border-t border-ink/15 pt-2">
          {!openNote && employee?.notes && (
            <button
              onClick={() => { setNoteDraft(employee?.notes ?? ""); setOpenNote(true); }}
              className="text-left w-full font-crimson italic text-[12px] text-paper hover:text-ink"
              title="Editar nota"
            >
              ✎ {employee.notes}
            </button>
          )}
          {!openNote && !employee?.notes && (
            <button
              onClick={() => { setNoteDraft(""); setOpenNote(true); }}
              className="tag text-graydk hover:text-ink"
            >+ adicionar nota</button>
          )}
          {openNote && (
            <div className="flex flex-col gap-1">
              <textarea
                autoFocus
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                rows={2}
                className="w-full border border-ink bg-bone px-2 py-1 font-mono text-[12px] resize-none"
                placeholder="nota interna sobre este contratado…"
              />
              <div className="flex gap-2 justify-end">
                <button
                  onClick={() => setOpenNote(false)}
                  className="btn btn-fire">cancelar</button>
                <button
                  onClick={() => { setNote(persona.id, noteDraft.trim()); setOpenNote(false); }}
                  className="btn btn-hire">salvar</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* status dot */}
      <span className={`absolute -top-2 -left-2 w-4 h-4 border-2 border-bone
        ${status === "hired"  ? "bg-red"   : ""}
        ${status === "fired"  ? "bg-graymd": ""}
        ${status === "available" ? "bg-ink" : ""}`} />

      {showReview && <ReviewModal personaId={persona.id} onClose={() => setShowReview(false)} />}
      {showPrompt && <AgentPromptEditor personaId={persona.id} onClose={() => setShowPrompt(false)} />}
    </div>
  );
}
